import { useState, useEffect } from 'react';
import { CameraFeed } from './components/Camera/CameraFeed';
import { TextDisplay } from './components/Output/TextDisplay';
import { SpeechSynthesis } from './components/Output/SpeechSynthesis';
import { ParticleSystem } from './components/Visualization/ParticleSystem';
import { CustomGestureTrainer } from './components/GestureRecognition/CustomGestureTrainer';
import { Settings } from './components/UI/Settings';
import { AuthModal } from './components/Auth/AuthModal';
import { AIContextPanel } from './components/AI/AIContextPanel';
import { MultiplayerRoom } from './components/Realtime/MultiplayerRoom';
import { CaregiverDashboard } from './components/Realtime/CaregiverDashboard';
import { useAppStore } from './store/appStore';
import { apiService } from './services/api.service';
import './App.css';

function App() {
  const { clearHistory, currentEmotion, customGestures, user, isAuthenticated, setUser, logout } = useAppStore();
  const [activeTab, setActiveTab] = useState<'main' | 'trainer' | 'settings' | 'ai' | 'multiplayer' | 'caregiver'>('main');
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = apiService.getToken();
    if (token && !user) {
      // Verify token and load user
      apiService.getProfile()
        .then((profile: any) => setUser(profile))
        .catch(() => apiService.clearToken());
    }
  }, []);

  const handleLogout = () => {
    apiService.clearToken();
    logout();
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div>
            <h1>AIX-Sync</h1>
            <p className="subtitle">Real-Time Collaboration - Phase 4</p>
          </div>
          <div className="auth-section">
            {isAuthenticated && user ? (
              <div className="user-menu">
                <span className="user-name">Hello, {user.name}</span>
                <button onClick={handleLogout} className="btn-logout">Logout</button>
              </div>
            ) : (
              <button onClick={() => setShowAuthModal(true)} className="btn-login">
                Login / Register
              </button>
            )}
          </div>
        </div>
        <nav className="app-nav">
          <button 
            className={activeTab === 'main' ? 'active' : ''}
            onClick={() => setActiveTab('main')}
          >
            Main
          </button>
          <button 
            className={activeTab === 'ai' ? 'active' : ''}
            onClick={() => setActiveTab('ai')}
          >
            AI & Translation
          </button>
          <button 
            className={activeTab === 'multiplayer' ? 'active' : ''}
            onClick={() => setActiveTab('multiplayer')}
          >
            Multiplayer
          </button>
          <button 
            className={activeTab === 'caregiver' ? 'active' : ''}
            onClick={() => setActiveTab('caregiver')}
          >
            Caregiver
          </button>
          <button 
            className={activeTab === 'trainer' ? 'active' : ''}
            onClick={() => setActiveTab('trainer')}
          >
            Custom Gestures ({customGestures.length})
          </button>
          <button 
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </nav>
      </header>

      <main className="app-main">
        {activeTab === 'main' && (
          <>
            <div className="left-panel">
              <CameraFeed />
              <SpeechSynthesis />
              {currentEmotion && (
                <div className="emotion-display">
                  <h3>Emotion Detected</h3>
                  <p className="emotion-name">{currentEmotion.emotion}</p>
                  <p className="emotion-confidence">
                    {(currentEmotion.confidence * 100).toFixed(0)}% confident
                  </p>
                </div>
              )}
            </div>

            <div className="center-panel">
              <ParticleSystem />
            </div>

            <div className="right-panel">
              <TextDisplay />
              <button 
                onClick={clearHistory}
                className="btn btn-secondary clear-btn"
              >
                Clear History
              </button>
            </div>
          </>
        )}

        {activeTab === 'ai' && (
          <div className="full-panel">
            <AIContextPanel />
          </div>
        )}

        {activeTab === 'multiplayer' && (
          <div className="full-panel">
            <MultiplayerRoom />
          </div>
        )}

        {activeTab === 'caregiver' && (
          <div className="full-panel">
            <CaregiverDashboard />
          </div>
        )}

        {activeTab === 'trainer' && (
          <div className="full-panel">
            <CustomGestureTrainer />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="full-panel">
            <Settings />
          </div>
        )}
      </main>

      <footer className="app-footer">
        <p>Phase 4 - Real-Time Collaboration</p>
        <p className="tech-stack">
          Supabase Realtime • Multiplayer Rooms • Emergency Alerts • Caregiver Monitoring
        </p>
        <p className="api-status">
          API: {isAuthenticated ? '🟢 Connected' : '🔴 Not Connected'}
        </p>
      </footer>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
}

export default App;
